import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  UsersIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  KeyIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  ShoppingCartIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const GestioneRuoliPage = () => {
  const [ruoliConfigurazione, setRuoliConfigurazione] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sezioniEspanse, setSezioniEspanse] = useState({
    admin: true,
    agenzia: true,
    team_leader: true,
    subagente: true,
    segnalatore: true
  });

  // Definizione dei ruoli utente
  const ruoliUtente = [
    {
      id: 'admin',
      nome: 'Amministratore',
      icona: ShieldCheckIcon,
      colore: 'red'
    },
    {
      id: 'agenzia',
      nome: 'Agenzia',
      icona: BuildingOfficeIcon,
      colore: 'blue'
    },
    {
      id: 'team_leader',
      nome: 'Team Leader',
      icona: UsersIcon,
      colore: 'green'
    },
    {
      id: 'subagente',
      nome: 'Subagente',
      icona: UserIcon,
      colore: 'purple'
    },
    {
      id: 'segnalatore',
      nome: 'Segnalatore',
      icona: UserPlusIcon,
      colore: 'yellow'
    }
  ];

  // Definizione delle pagine con icone
  const pagineDisponibili = [
    {
      id: 'clienti',
      nome: 'Clienti',
      icona: UserGroupIcon,
      descrizione: 'Gestione clienti e lead',
      sottopagine: [
        { id: 'lista-clienti', nome: 'Lista Clienti', descrizione: 'Visualizzazione e gestione clienti' },
        { id: 'gestione-lead', nome: 'Gestione Lead', descrizione: 'Gestione lead e conversioni' }
      ]
    },
    {
      id: 'quotazioni',
      nome: 'Quotazioni',
      icona: ClipboardDocumentListIcon,
      descrizione: 'Preventivi, pratiche e veicoli',
      sottopagine: [
        { id: 'pratiche', nome: 'Pratiche', descrizione: 'Gestione pratiche e contratti' },
        { id: 'lista-quotazioni', nome: 'Lista Quotazioni', descrizione: 'Visualizzazione quotazioni' },
        { id: 'lista-veicoli', nome: 'Lista Veicoli', descrizione: 'Catalogo veicoli disponibili' },
        { id: 'gestione-offerte', nome: 'Gestione Offerte', descrizione: 'Configurazione offerte vetture' },
        { id: 'offerte-pubbliche', nome: 'Offerte Pubbliche', descrizione: 'Visualizzazione offerte pubbliche' },
        { id: 'societa-noleggio', nome: 'Società di Noleggio', descrizione: 'Gestione società partner' }
      ]
    },
    {
      id: 'rete-multilivello',
      nome: 'Rete Multilivello',
      icona: BuildingOfficeIcon,
      descrizione: 'Gestione della rete commerciale',
      sottopagine: [
        { id: 'dashboard-rete', nome: 'Dashboard Rete', descrizione: 'Visualizzazione struttura rete' },
        { id: 'gestione-utenti', nome: 'Gestione Utenti', descrizione: 'Amministrazione utenti rete' },
        { id: 'gestione-ruoli', nome: 'Gestione Ruoli', descrizione: 'Configurazione autorizzazioni' }
      ]
    },
    {
      id: 'gestione',
      nome: 'Gestione',
      icona: CurrencyEuroIcon,
      descrizione: 'Funzioni amministrative',
      sottopagine: [
        { id: 'contratti', nome: 'Contratti', descrizione: 'Gestione contratti' },
        { id: 'provvigioni-contratti', nome: 'Provvigioni Contratti', descrizione: 'Gestione provvigioni' }
      ]
    },
    {
      id: 'documenti',
      nome: 'Documenti Interni',
      icona: DocumentTextIcon,
      descrizione: 'Documentazione e tutorial',
      sottopagine: [
        { id: 'tutorial', nome: 'Tutorial', descrizione: 'Guide e tutorial sistema' },
        { id: 'marketing', nome: 'Marketing', descrizione: 'Materiale promozionale e commerciale' }
      ]
    },
    {
      id: 'statistiche',
      nome: 'Statistiche',
      icona: ChartBarIcon,
      descrizione: 'Report e analisi',
      sottopagine: [
        { id: 'statistiche-fatturato', nome: 'Statistiche Fatturato', descrizione: 'Analisi fatturato' },
        { id: 'statistiche-pratiche', nome: 'Statistiche Pratiche', descrizione: 'Analisi pratiche' }
      ]
    }
  ];

  // Configurazione predefinita per reset individuale
  const configurazioneDefault = {
    admin: {
      clienti: { abilitato: true, sottopagine: { 'lista-clienti': true, 'gestione-lead': true } },
      quotazioni: { abilitato: true, sottopagine: { 'pratiche': true, 'lista-quotazioni': true, 'lista-veicoli': true, 'gestione-offerte': true, 'offerte-pubbliche': true, 'societa-noleggio': true } },
      'rete-multilivello': { abilitato: true, sottopagine: { 'dashboard-rete': true, 'gestione-utenti': true, 'gestione-ruoli': true } },
      gestione: { abilitato: true, sottopagine: { 'contratti': true, 'provvigioni-contratti': true } },
      documenti: { abilitato: true, sottopagine: { 'tutorial': true, 'marketing': true } },
      statistiche: { abilitato: true, sottopagine: { 'statistiche-fatturato': true, 'statistiche-pratiche': true } }
    },
    agenzia: {
      clienti: { abilitato: true, sottopagine: { 'lista-clienti': true, 'gestione-lead': true } },
      quotazioni: { abilitato: true, sottopagine: { 'pratiche': true, 'lista-quotazioni': true, 'lista-veicoli': true, 'gestione-offerte': true, 'offerte-pubbliche': true, 'societa-noleggio': false } },
      'rete-multilivello': { abilitato: true, sottopagine: { 'dashboard-rete': true, 'gestione-utenti': true, 'gestione-ruoli': false } },
      gestione: { abilitato: true, sottopagine: { 'contratti': true, 'provvigioni-contratti': true } },
      documenti: { abilitato: true, sottopagine: { 'tutorial': true, 'marketing': true } },
      statistiche: { abilitato: true, sottopagine: { 'statistiche-fatturato': true, 'statistiche-pratiche': true } }
    },
    team_leader: {
      clienti: { abilitato: true, sottopagine: { 'lista-clienti': true, 'gestione-lead': true } },
      quotazioni: { abilitato: true, sottopagine: { 'pratiche': true, 'lista-quotazioni': true, 'lista-veicoli': true, 'gestione-offerte': false, 'offerte-pubbliche': true, 'societa-noleggio': false } },
      'rete-multilivello': { abilitato: true, sottopagine: { 'dashboard-rete': true, 'gestione-utenti': false, 'gestione-ruoli': false } },
      gestione: { abilitato: false, sottopagine: { 'contratti': false, 'provvigioni-contratti': false } },
      documenti: { abilitato: true, sottopagine: { 'tutorial': true, 'marketing': true } },
      statistiche: { abilitato: true, sottopagine: { 'statistiche-fatturato': true, 'statistiche-pratiche': true } }
    },
    subagente: {
      clienti: { abilitato: true, sottopagine: { 'lista-clienti': true, 'gestione-lead': true } },
      quotazioni: { abilitato: true, sottopagine: { 'pratiche': true, 'lista-quotazioni': true, 'lista-veicoli': true, 'gestione-offerte': false, 'offerte-pubbliche': true, 'societa-noleggio': false } },
      'rete-multilivello': { abilitato: false, sottopagine: { 'dashboard-rete': false, 'gestione-utenti': false, 'gestione-ruoli': false } },
      gestione: { abilitato: false, sottopagine: { 'contratti': false, 'provvigioni-contratti': false } },
      documenti: { abilitato: true, sottopagine: { 'tutorial': true, 'marketing': true } },
      statistiche: { abilitato: true, sottopagine: { 'statistiche-fatturato': false, 'statistiche-pratiche': true } }
    },
    segnalatore: {
      clienti: { abilitato: true, sottopagine: { 'lista-clienti': false, 'gestione-lead': true } },
      quotazioni: { abilitato: true, sottopagine: { 'pratiche': false, 'lista-quotazioni': true, 'lista-veicoli': true, 'gestione-offerte': false, 'offerte-pubbliche': true, 'societa-noleggio': false } },
      'rete-multilivello': { abilitato: false, sottopagine: { 'dashboard-rete': false, 'gestione-utenti': false, 'gestione-ruoli': false } },
      gestione: { abilitato: false, sottopagine: { 'contratti': false, 'provvigioni-contratti': false } },
      documenti: { abilitato: true, sottopagine: { 'tutorial': true, 'marketing': true } },
      statistiche: { abilitato: true, sottopagine: { 'statistiche-fatturato': false, 'statistiche-pratiche': false } }
    }
  };

  useEffect(() => {
    caricaConfigurazioneRuoli();
  }, []);

  const caricaConfigurazioneRuoli = async () => {
    try {
      console.log('🔄 Caricamento configurazione ruoli...');
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/network/ruoli/configurazione', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 Configurazione ricevuta:', data);

      if (data.successo) {
        setRuoliConfigurazione(data.data);
        console.log('✅ Configurazione ruoli caricata con successo');
      } else {
        throw new Error(data.error || 'Errore nel caricamento configurazione');
      }

    } catch (error) {
      console.error('❌ Errore nel caricamento configurazione ruoli:', error);
      toast.error('Errore nel caricamento della configurazione ruoli');
      
      // Fallback alla configurazione predefinita
      setRuoliConfigurazione(configurazioneDefault);
    } finally {
      setLoading(false);
    }
  };

  const salvaConfigurazione = async () => {
    try {
      setSalvando(true);
      console.log('💾 Salvataggio configurazione ruoli...');

      const token = localStorage.getItem('token');
      const response = await fetch('/api/network/ruoli/configurazione', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configurazione: ruoliConfigurazione
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📤 Risposta salvataggio:', data);

      if (data.successo) {
        toast.success('Configurazione ruoli salvata con successo');
        console.log('✅ Configurazione salvata');
      } else {
        throw new Error(data.error || 'Errore nel salvataggio');
      }

    } catch (error) {
      console.error('❌ Errore nel salvataggio configurazione:', error);
      toast.error('Errore nel salvataggio della configurazione');
    } finally {
      setSalvando(false);
    }
  };

  const toggleSezione = (ruolo, pagina) => {
    setRuoliConfigurazione(prev => {
      const nuovaConfig = { ...prev };
      const paginaConfig = nuovaConfig[ruolo][pagina];
      
      // Inverti lo stato abilitato
      paginaConfig.abilitato = !paginaConfig.abilitato;
      
      // Se disabilitato, disabilita anche tutte le sottopagine
      if (!paginaConfig.abilitato) {
        Object.keys(paginaConfig.sottopagine).forEach(sottopagina => {
          paginaConfig.sottopagine[sottopagina] = false;
        });
      }
      
      return nuovaConfig;
    });
  };

  const toggleSottopagina = (ruolo, pagina, sottopagina) => {
    setRuoliConfigurazione(prev => {
      const nuovaConfig = { ...prev };
      nuovaConfig[ruolo][pagina].sottopagine[sottopagina] = !nuovaConfig[ruolo][pagina].sottopagine[sottopagina];
      return nuovaConfig;
    });
  };

  const toggleEspansione = (ruolo) => {
    setSezioniEspanse(prev => ({
      ...prev,
      [ruolo]: !prev[ruolo]
    }));
  };

  const resetRuolo = (ruolo) => {
    setRuoliConfigurazione(prev => ({
      ...prev,
      [ruolo]: configurazioneDefault[ruolo]
    }));
    toast.info(`Configurazione di ${ruoliUtente.find(r => r.id === ruolo)?.nome} ripristinata ai valori predefiniti`);
  };

  const getRuolo = (id) => {
    return ruoliUtente.find(r => r.id === id);
  };

  const getColoreIcona = (colore) => {
    const colori = {
      red: 'text-red-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      yellow: 'text-yellow-600'
    };
    return colori[colore] || 'text-gray-600';
  };

  const getColoreSfondo = (colore) => {
    const colori = {
      red: 'bg-red-100',
      blue: 'bg-blue-100',
      green: 'bg-green-100',
      purple: 'bg-purple-100',
      yellow: 'bg-yellow-100'
    };
    return colori[colore] || 'bg-gray-100';
  };

  const contaPagineAbilitate = (ruolo) => {
    const config = ruoliConfigurazione[ruolo];
    if (!config) return 0;
    
    return Object.values(config).filter(pagina => pagina.abilitato).length;
  };

  const contaSottopagineAbilitate = (ruolo, paginaId) => {
    const config = ruoliConfigurazione[ruolo]?.[paginaId]?.sottopagine;
    if (!config) return 0;
    
    return Object.values(config).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento configurazione ruoli...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestione Ruoli e Autorizzazioni</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Configura le autorizzazioni per visualizzare le pagine del sistema per ogni ruolo utente
                </p>
              </div>
            </div>
            
            <button
              onClick={salvaConfigurazione}
              disabled={salvando}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {salvando ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Salva Configurazione
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info generale */}
        <div className="bg-white rounded-lg shadow-sm border mb-8">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-600" />
              Riepilogo Configurazione Ruoli
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {ruoliUtente.map((ruolo) => {
                const Icona = ruolo.icona;
                const pagineAbilitate = contaPagineAbilitate(ruolo.id);
                const totalePagine = pagineDisponibili.length;
                const percentuale = Math.round((pagineAbilitate / totalePagine) * 100);
                
                return (
                  <div key={ruolo.id} className={`${getColoreSfondo(ruolo.colore)} rounded-lg p-4 border-2 border-transparent hover:border-gray-200 transition-colors`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`p-2 ${getColoreSfondo(ruolo.colore)} rounded-lg`}>
                        <Icona className={`h-5 w-5 ${getColoreIcona(ruolo.colore)}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{ruolo.nome}</h3>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Accesso:</span>
                        <span className={`font-medium ${percentuale >= 80 ? 'text-green-600' : percentuale >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {percentuale}%
                        </span>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            percentuale >= 80 ? 'bg-green-500' : 
                            percentuale >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${percentuale}%` }}
                        ></div>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        {pagineAbilitate}/{totalePagine} sezioni abilitate
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Configurazione dettagliata per ogni ruolo */}
        <div className="space-y-6">
          {ruoliUtente.map((ruolo) => {
            const Icona = ruolo.icona;
            const isEspanso = sezioniEspanse[ruolo.id];
            const config = ruoliConfigurazione[ruolo.id];
            
            return (
              <div key={ruolo.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Header del ruolo */}
                <div 
                  className={`px-6 py-4 ${getColoreSfondo(ruolo.colore)} border-b cursor-pointer hover:bg-opacity-70 transition-colors`}
                  onClick={() => toggleEspansione(ruolo.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 bg-white rounded-lg shadow-sm`}>
                        <Icona className={`h-6 w-6 ${getColoreIcona(ruolo.colore)}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{ruolo.nome}</h3>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          resetRuolo(ruolo.id);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-colors"
                        title="Ripristina configurazione predefinita"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                      </button>
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">
                          {contaPagineAbilitate(ruolo.id)}/{pagineDisponibili.length} sezioni
                        </span>
                        {isEspanso ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenuto espandibile */}
                {isEspanso && config && (
                  <div className="p-6">
                    <div className="space-y-6">
                      {pagineDisponibili.map((pagina) => {
                        const IconaPagina = pagina.icona;
                        const paginaConfig = config[pagina.id];
                        const sottopagineAbilitate = contaSottopagineAbilitate(ruolo.id, pagina.id);
                        const totaleSottopagine = pagina.sottopagine.length;
                        
                        return (
                          <div key={pagina.id} className="border border-gray-200 rounded-lg p-4">
                            {/* Header pagina */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <IconaPagina className="h-5 w-5 text-gray-600" />
                                <div>
                                  <h4 className="font-medium text-gray-900">{pagina.nome}</h4>
                                  <p className="text-sm text-gray-500">{pagina.descrizione}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <span className="text-sm text-gray-600">
                                  {sottopagineAbilitate}/{totaleSottopagine} sottosezioni
                                </span>
                                <button
                                  onClick={() => toggleSezione(ruolo.id, pagina.id)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    paginaConfig?.abilitato ? 'bg-blue-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      paginaConfig?.abilitato ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>

                            {/* Sottopagine */}
                            {paginaConfig?.abilitato && (
                              <div className="ml-8 space-y-3">
                                {pagina.sottopagine.map((sottopagina) => (
                                  <div key={sottopagina.id} className="flex items-center justify-between py-2">
                                    <div>
                                      <span className="text-sm font-medium text-gray-900">{sottopagina.nome}</span>
                                      <p className="text-xs text-gray-500">{sottopagina.descrizione}</p>
                                    </div>
                                    
                                    <button
                                      onClick={() => toggleSottopagina(ruolo.id, pagina.id, sottopagina.id)}
                                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                        paginaConfig.sottopagine?.[sottopagina.id] ? 'bg-green-600' : 'bg-gray-200'
                                      }`}
                                    >
                                      <span
                                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                          paginaConfig.sottopagine?.[sottopagina.id] ? 'translate-x-5' : 'translate-x-1'
                                        }`}
                                      />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Messaggio quando sezione disabilitata */}
                            {!paginaConfig?.abilitato && (
                              <div className="ml-8 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="text-sm text-gray-500 flex items-center">
                                  <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-gray-400" />
                                  Sezione disabilitata - Tutte le sottosezioni sono automaticamente disabilitate
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GestioneRuoliPage; 